// 2026-27 season accommodation rates for the public /quote tool.
//
// Four Points' rates repeat by calendar month regardless of year (the hotel's
// standard rate card) — everyone else's rates are tied to specific years/dates
// since their pricing genuinely differs between the Sep-Dec 2026 and
// Jan-Jul 2027 halves of the season (or, for La Nucia, to specific stay
// windows entirely). Hardcoded rather than admin-editable: these are a fixed
// season's numbers exactly as given, and a generic date-range pricing editor
// for a table this irregular (month rules, year-specific rules, exact-date
// rules, a room-count threshold, board-type variants) is a much bigger,
// riskier build than reviewable constants for a table that rarely changes.

export const HOTELS = [
  { key: 'four_points',    name: 'Four Points by Sheraton', boardTypes: ['half_board', 'full_board'], roomTypes: ['sharing', 'single'] },
  { key: 'la_nucia',       name: 'La Nucia Sports Village',  boardTypes: ['full_board'],               roomTypes: ['double', 'triple'] },
  { key: 'castilla',       name: 'Hotel Castilla',           boardTypes: ['half_board'],               roomTypes: ['double', 'triple'] },
  { key: 'alicante_golf',  name: 'Alicante Golf',            boardTypes: ['half_board'],               roomTypes: ['double', 'triple'] },
];

export const ROOM_TYPE_LABELS = { sharing: 'Sharing', single: 'Single', double: 'Double', triple: 'Triple' };
export const BOARD_TYPE_LABELS = { half_board: 'Half board', full_board: 'Full board' };
export const ROOM_OCCUPANCY = { sharing: 2, single: 1, double: 2, triple: 3 };

function ym(dateStr) {
  // 'YYYY-MM-DD' -> { year, month } (month 1-12)
  const [y, m] = (dateStr || '').split('-').map(Number);
  return { year: y, month: m };
}

// Month-of-year rule (any year) — Four Points.
function monthRule(fromMonth, toMonth, prices) {
  return { type: 'month', fromMonth, toMonth, prices };
}
// Year+month rule — Alicante Golf, Castilla.
function yearMonthRule(fromYear, fromMonth, toYear, toMonth, prices) {
  return { type: 'yearMonth', fromYear, fromMonth, toYear, toMonth, prices };
}
// Exact date-range rule — La Nucia. Prices here are the TOTAL per-person
// figure for that specific window already, not a nightly rate.
function exactDateRule(fromDate, toDate, prices) {
  return { type: 'exactDate', fromDate, toDate, prices, isTotal: true };
}
function unavailableMonthRule(fromMonth, toMonth, reason) {
  return { type: 'month', fromMonth, toMonth, unavailable: true, reason };
}
function unavailableYearMonthRule(fromYear, fromMonth, toYear, toMonth, reason) {
  return { type: 'yearMonth', fromYear, fromMonth, toYear, toMonth, unavailable: true, reason };
}
function unavailableDateRule(fromDate, toDate, reason) {
  return { type: 'exactDate', fromDate, toDate, unavailable: true, reason };
}

const RATES = {
  four_points: {
    half_board: [
      monthRule(1, 5, { sharing: 69, single: 109 }),
      monthRule(6, 6, { sharing: 87, single: 134 }),
      monthRule(7, 7, { sharing: 94, single: 149, sharingOver25: 109, singleOver25: 174 }),
      unavailableMonthRule(8, 8, 'On request — contact MST for August pricing'),
      monthRule(9, 9, { sharing: 87, single: 134 }),
      monthRule(10, 12, { sharing: 69, single: 109 }),
    ],
    full_board: [
      monthRule(1, 4, { sharing: 84, single: 124 }),
      monthRule(5, 6, { sharing: 102, single: 149 }),
      monthRule(7, 7, { sharing: 109, single: 164, sharingOver25: 124, singleOver25: 189 }),
      unavailableMonthRule(8, 8, 'On request — contact MST for August pricing'),
      monthRule(9, 9, { sharing: 102, single: 149 }),
      monthRule(10, 12, { sharing: 84, single: 124 }),
    ],
  },
  alicante_golf: {
    half_board: [
      yearMonthRule(2026, 9, 2026, 10, { double: 75, triple: 60 }),
      yearMonthRule(2026, 11, 2026, 12, { double: 65, triple: 55 }),
      yearMonthRule(2027, 1, 2027, 1, { double: 70, triple: 56.67 }),
      yearMonthRule(2027, 2, 2027, 5, { double: 72.50, triple: 58.33 }),
      // Jun-Jul is the last distinct tier MST gave us — the summer-peak rate
      // carries through Aug-Sep too rather than going unpriced.
      yearMonthRule(2027, 6, 2027, 9, { double: 87.50, triple: 68.33 }),
    ],
  },
  castilla: {
    half_board: [
      unavailableYearMonthRule(2026, 1, 2026, 12, 'No pricing available before January 2027'),
      yearMonthRule(2027, 1, 2027, 1, { double: 70, triple: 56.67 }),
      yearMonthRule(2027, 2, 2027, 5, { double: 72.50, triple: 58.33 }),
      // Jun-Jul is the last distinct tier MST gave us — the summer-peak rate
      // carries through Aug-Sep too rather than going unpriced.
      yearMonthRule(2027, 6, 2027, 9, { double: 87.50, triple: 68.33 }),
    ],
  },
  la_nucia: {
    full_board: [
      exactDateRule('2026-10-17', '2026-10-17', { double: 58 }),
      exactDateRule('2026-10-21', '2026-10-23', { double: 68, triple: 45.33 }),
      exactDateRule('2026-10-30', '2026-10-30', { double: 78 }),
      exactDateRule('2027-01-07', '2027-01-09', { triple: 63.67 }),
      exactDateRule('2027-03-25', '2027-03-28', { triple: 72.67 }),
      unavailableDateRule('2026-09-01', '2026-09-06', 'No availability'),
      unavailableDateRule('2026-09-26', '2026-10-03', 'No availability'),
      unavailableDateRule('2027-04-11', '2027-04-12', 'Limited availability — contact MST'),
    ],
  },
};

function ruleMatches(rule, checkInDate) {
  const { year, month } = ym(checkInDate);
  if (rule.type === 'month') return month >= rule.fromMonth && month <= rule.toMonth;
  if (rule.type === 'yearMonth') {
    const afterStart = year > rule.fromYear || (year === rule.fromYear && month >= rule.fromMonth);
    const beforeEnd = year < rule.toYear || (year === rule.toYear && month <= rule.toMonth);
    return afterStart && beforeEnd;
  }
  if (rule.type === 'exactDate') return checkInDate >= rule.fromDate && checkInDate <= rule.toDate;
  return false;
}

/**
 * Look up the per-person accommodation rate for a hotel/board/room combination
 * on a given check-in date. Returns:
 *   { available: true, perPerson, isTotal }  — isTotal means don't × nights (La Nucia)
 *   { available: false, reason }
 */
export function getHotelRate(hotelKey, boardType, roomType, checkInDate, nights, travellers) {
  const table = RATES[hotelKey]?.[boardType];
  if (!table || !checkInDate) return { available: false, reason: 'Select a date' };

  const rule = table.find((r) => ruleMatches(r, checkInDate));
  if (!rule) return { available: false, reason: 'No pricing for this date' };
  if (rule.unavailable) return { available: false, reason: rule.reason };

  let perPerson = rule.prices[roomType];
  if (perPerson == null) return { available: false, reason: 'Room type not offered for this date' };

  // Four Points July room-count threshold
  if (rule.prices[`${roomType}Over25`] != null) {
    const occupancy = ROOM_OCCUPANCY[roomType] || 1;
    const roomCount = Math.ceil((Number(travellers) || 0) / occupancy);
    if (roomCount > 25) perPerson = rule.prices[`${roomType}Over25`];
  }

  return { available: true, perPerson, isTotal: !!rule.isTotal };
}

export function hotelBoardTypes(hotelKey) {
  return HOTELS.find((h) => h.key === hotelKey)?.boardTypes || [];
}
export function hotelRoomTypes(hotelKey) {
  return HOTELS.find((h) => h.key === hotelKey)?.roomTypes || [];
}

/**
 * Whether a hotel has pricing for ANY of its board/room combinations on a
 * given check-in date (e.g. La Nucia is only priced for a handful of
 * specific fixture-weekend dates — on any other date every combination is
 * unavailable, so the hotel itself shouldn't be offered as an option).
 */
export function isHotelAvailableOn(hotelKey, checkInDate, nights, travellers) {
  if (!checkInDate) return true; // no date picked yet — don't hide anything
  const boardTypes = hotelBoardTypes(hotelKey);
  const roomTypes = hotelRoomTypes(hotelKey);
  return boardTypes.some((bt) => roomTypes.some((rt) =>
    getHotelRate(hotelKey, bt, rt, checkInDate, nights, travellers).available
  ));
}
